import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'

export const formActorTypeSchema = z
  .enum(['STAFF', 'SYSTEM', 'CLIENT', 'DEVICE', 'PUBLIC_LINK'])
  .register(asEnumType, {
    name: 'FormActorType',
    description: 'Who completed or created a form assignment',
  })

export const formCompletedViaSchema = z
  .enum(['STAFF_CORE', 'KIOSK', 'MEMBER_APP', 'EMBED', 'PUBLIC_LINK', 'SIGNUP', 'EXTERNAL'])
  .register(asEnumType, {
    name: 'FormCompletedVia',
    description: 'The channel a form submission was completed through',
  })

export const formRequiredBeforeSchema = z
  .enum(['NONE', 'BOOKING', 'TANNING'])
  .register(asEnumType, {
    name: 'FormRequiredBefore',
    description: 'The activity a form assignment must be completed before',
  })

export const formAssignmentSourceSchema = z
  .enum(['STAFF', 'SYSTEM', 'SIGNUP'])
  .register(asEnumType, {
    name: 'FormAssignmentSource',
    description: 'What created a form assignment',
  })

export const formWorkflowErrorCodeSchema = z
  .enum([
    'FORM_REVISION_CONFLICT',
    'FORM_REFRESH_REQUIRED',
    'FORM_ANSWERS_INVALID',
    'SCORED_FORM_AGGREGATE_SAVE_REQUIRED',
    'SCORE_BANDS_INVALID',
    'REQUIREMENT_CHANGE_REQUIRED',
    'FORM_ALREADY_COMPLETED',
    'IDEMPOTENCY_CONFLICT',
    'FORM_ASSIGNMENT_CANCELLED',
    'FORM_RESULT_IMMUTABLE',
    'FORM_SIGNUP_SIGNATURE_UNSUPPORTED',
    'HANDOFF_EXPIRED',
    'HANDOFF_NOT_CURRENT',
    'DEVICE_BUSY',
    'DEVICE_OFFLINE',
    'DEVICE_REVOKED',
    'DEVICE_CAPABILITY_REQUIRED',
    'SKIN_TYPE_REQUIRED',
    'SKIN_TYPE_DUE',
    'SKIN_TYPE_CONFIGURATION_ERROR',
    'SKIN_TYPE_OVERRIDE_FORBIDDEN',
    'SKIN_TYPE_OVERRIDE_REASON_REQUIRED',
    'RESULT_MAPPING_INVALID',
    'SIGNATURE_UPLOAD_INVALID',
    'FEATURE_UNAVAILABLE',
    'FEATURE_NOT_ENTITLED',
    'NOT_FOUND',
    'CLIENT_EMAIL_MISSING',
    'COPY_NOT_SENT',
    'EXTERNAL_RESULT_INVALID',
    'IDENTITY_MISMATCH',
    'IDENTITY_LOCKED',
  ])
  .register(asEnumType, {
    name: 'FormWorkflowErrorCode',
    description: 'Machine-readable outcome codes for the form workflow',
  })

export const formCancellationReasonSchema = z
  .enum([
    'STAFF_WITHDRAWN',
    'SCORING_ENABLED_REISSUE',
    'REQUIREMENT_REPLACED',
    'CLIENT_MERGED',
    'RESULT_RECORDED_EXTERNALLY',
  ])
  .register(asEnumType, {
    name: 'FormCancellationReason',
    description: 'Why an uncompleted form assignment was withdrawn',
  })

export const formWorkflowEffectTypeSchema = z.enum([
  'RESULT_PROPERTY_PROJECTION',
  'HANDOFF_SIGNAL',
  'NOTIFY_MEMBER',
  'SEND_LINK',
])

export const formWorkflowEffectStatusSchema = z.enum([
  'PENDING',
  'LEASED',
  'DONE',
  'FAILED',
  'STALE',
])

export const formSignatureAssetStatusSchema = z.enum([
  'UPLOADING',
  'READY',
  'CONSUMED',
  'REJECTED',
  'PURGING',
])

export const formPurgeReasonSchema = z.enum(['RETENTION_EXPIRED'])

export const formSignatureFormatSchema = z.enum(['PNG']).register(asEnumType, {
  name: 'FormSignatureFormat',
  description: 'Image format of an uploaded form signature',
})

export const FORM_SIGNATURE_MAX_BYTES = 256 * 1024
export const FORM_SIGNATURE_UPLOAD_TTL_MS = 5 * 60 * 1000
export const FORM_SIGNATURE_READ_URL_TTL_MS = 5 * 60 * 1000
export const FORM_SIGNATURE_ANSWER_VERSION = 1
export const FORM_SIGNATURE_SIGNUP_ATTEMPT_TTL_MS = 30 * 60 * 1000
// An upload URL lives five minutes, but a READY signature waits for the rest of the form to be
// answered, so only assets left unconsumed a full day past their expiry count as abandoned.
export const FORM_SIGNATURE_ABANDONED_GRACE_MS = 24 * 60 * 60 * 1000

export const formSignatureAnswerSchema = z.object({
  v: z.literal(FORM_SIGNATURE_ANSWER_VERSION),
  signatureAssetId: z.uuid(),
  signerName: z.string().trim().min(1).max(255),
  declarationAccepted: z.literal(true),
})

export const resultProjectionPayloadSchema = z.object({
  formSubmissionId: z.string(),
  siteFormId: z.string(),
  siteCustomPropertyId: z.string(),
  resultMappingGeneration: z.number().int().nullable(),
  resultBandId: z.string(),
})

export const handoffEffectPayloadSchema = z.object({
  handoffId: z.string(),
})

export const initFormSignatureUploadInputSchema = z.object({
  formSubmissionId: z.string().min(1).max(36),
  siteFormQuestionId: z.string().min(1).max(36),
  format: formSignatureFormatSchema.optional(),
})

export const formSignatureUploadHeaderSchema = z
  .object({
    name: z.string(),
    value: z.string(),
  })
  .register(asObjectType, {
    name: 'FormSignatureUploadHeader',
    description: 'A header the signature upload request must carry',
  })

export const formSignatureUploadTicketSchema = z
  .object({
    uploadId: z.string(),
    uploadUrl: z.string(),
    expiresAt: z.date(),
    uploadHeaders: z.array(formSignatureUploadHeaderSchema),
  })
  .register(asObjectType, {
    name: 'FormSignatureUploadTicket',
    description:
      'A short-lived URL for uploading one signature image; send it back as signatureAssetId',
  })

export const finalizeFormSignatureUploadInputSchema = z.object({
  uploadId: z.string().min(1).max(36),
})

export const formSignatureAssetViewSchema = z
  .object({
    signatureAssetId: z.string(),
    status: z.string(),
    byteCount: z.number().int().nullable(),
    checksumSha256: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'FormSignatureAsset',
    description: 'A verified signature image held as private form evidence',
  })

export const formSignatureReadUrlSchema = z
  .object({
    url: z.string(),
    expiresAt: z.date(),
  })
  .register(asObjectType, {
    name: 'FormSignatureReadUrl',
    description: 'A short-lived URL for viewing a signature image',
  })

export const emailFormSubmissionCopyResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
  })
  .register(asObjectType, {
    name: 'EmailFormSubmissionCopyResponse',
    description: 'Outcome of emailing a client a copy of their completed form',
  })

export const formWorkflowEffectFailureSchema = z
  .object({
    effectId: z.string(),
    effectType: z.string(),
    clientId: z.string(),
    formSubmissionId: z.string().nullable(),
    attemptCount: z.number().int(),
    lastErrorCode: z.string().nullable(),
    updatedAt: z.date(),
  })
  .register(asObjectType, {
    name: 'FormWorkflowEffectFailure',
    description: 'A form follow-up action that stopped retrying and needs attention',
  })

export const retryFormWorkflowEffectsResponseSchema = z
  .object({
    retriedCount: z.number().int(),
  })
  .register(asObjectType, {
    name: 'RetryFormWorkflowEffectsResponse',
    description: 'How many failed form follow-up actions were queued again',
  })

export const FORM_WORKFLOW_ERROR_CODE = formWorkflowErrorCodeSchema.enum
export const FORM_ACTOR_TYPE = formActorTypeSchema.enum
export const FORM_COMPLETED_VIA = formCompletedViaSchema.enum
export const FORM_REQUIRED_BEFORE = formRequiredBeforeSchema.enum
export const FORM_ASSIGNMENT_SOURCE = formAssignmentSourceSchema.enum
export const FORM_CANCELLATION_REASON = formCancellationReasonSchema.enum
export const FORM_WORKFLOW_EFFECT_TYPE = formWorkflowEffectTypeSchema.enum
export const FORM_WORKFLOW_EFFECT_STATUS = formWorkflowEffectStatusSchema.enum
export const FORM_SIGNATURE_ASSET_STATUS = formSignatureAssetStatusSchema.enum
export const FORM_PURGE_REASON = formPurgeReasonSchema.enum

export type FormActorType = z.infer<typeof formActorTypeSchema>
export type FormCompletedVia = z.infer<typeof formCompletedViaSchema>
export type FormRequiredBefore = z.infer<typeof formRequiredBeforeSchema>
export type FormAssignmentSource = z.infer<typeof formAssignmentSourceSchema>
export type FormWorkflowErrorCode = z.infer<typeof formWorkflowErrorCodeSchema>
export type FormCancellationReason = z.infer<typeof formCancellationReasonSchema>
export type FormWorkflowEffectType = z.infer<typeof formWorkflowEffectTypeSchema>
export type FormWorkflowEffectStatus = z.infer<typeof formWorkflowEffectStatusSchema>
export type FormSignatureAssetStatus = z.infer<typeof formSignatureAssetStatusSchema>
export type FormPurgeReason = z.infer<typeof formPurgeReasonSchema>
export type FormSignatureAnswer = z.infer<typeof formSignatureAnswerSchema>
export type ResultProjectionPayload = z.infer<typeof resultProjectionPayloadSchema>
export type HandoffEffectPayload = z.infer<typeof handoffEffectPayloadSchema>
export type InitFormSignatureUploadInput = z.infer<typeof initFormSignatureUploadInputSchema>
export type FormSignatureUploadTicket = z.infer<typeof formSignatureUploadTicketSchema>
export type FormSignatureAssetView = z.infer<typeof formSignatureAssetViewSchema>
export type FormSignatureReadUrl = z.infer<typeof formSignatureReadUrlSchema>
export type FormWorkflowEffectFailure = z.infer<typeof formWorkflowEffectFailureSchema>
